import React from 'react'
import { useCurrentPng } from 'recharts-to-png'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  Label,
  ReferenceLine,
} from 'recharts'
import { LINE_CHART_DATA_POINT_WIDTH } from 'constants/chartConstants'
import { ChartDownloadButton, Loader } from 'components'
import { formatDateString, formatNumber, FormatNumberOptions } from 'utils/utilities'

export type CustomLineProps = {
  yAxisId?: 'left' | 'right'
  dataKey: string
  dataColor: string
  dataLabel?: string
  dataFormat?: FormatNumberOptions
  hideLegend?: boolean
}[]

export type CustomLineChartProps = {
  data: {}[]
  title: React.ReactNode
  subTitle?: React.ReactNode
  xAxisDataKey: string
  labelX?: string
  lines: CustomLineProps
  labelYLeft?: string
  labelYRight?: string
  colorYLeft?: string
  colorYRight?: string
  formatYLeft?: FormatNumberOptions
  formatYRight?: FormatNumberOptions
  verticalFill?: string[]
  tooltip?: JSX.Element | null
  targetValue?: number
  fileName: string
  disableRightYAxis?: boolean
}

export const CustomLineChart = (props: CustomLineChartProps) => {
  const {
    data,
    title,
    subTitle,
    xAxisDataKey,
    labelX,
    lines,
    labelYLeft,
    labelYRight,
    colorYLeft = 'green',
    colorYRight = 'green',
    formatYLeft = { format: 'abbr' },
    formatYRight = { format: 'abbr' },
    verticalFill,
    tooltip,
    targetValue,
    fileName,
    disableRightYAxis,
  } = props

  const [calculatedWidth, setCalculatedWidth] = React.useState(0)
  const [getPng, { ref, isLoading }] = useCurrentPng()

  const measuredRef = React.useCallback(
    (node: HTMLDivElement) => {
      if (node !== null) {
        const clientWidth = node.getBoundingClientRect().width
        const dataWidth = data.length * LINE_CHART_DATA_POINT_WIDTH
        setCalculatedWidth(dataWidth < clientWidth ? clientWidth : dataWidth)
      }
    },
    [data.length]
  )

  const CustomTooltip = ({ active, payload, label }: any) => {
    const lineData = lines.find((lineItem) =>
      payload.some((payloadItem: any) => lineItem.dataKey === payloadItem.dataKey)
    )

    if (active && payload?.length) {
      return (
        <div className="bg-white-95 p-4">
          <h1 className="text-xl">{formatDateString(label)}</h1>
          {payload.map(
            (item: any) =>
              item.name && (
                <div key={item.dataKey}>
                  <p style={{ color: item.color }}>{item.name}</p>
                  <p>{formatNumber(item.payload?.[item.dataKey], lineData?.dataFormat) ?? '-'}</p>
                </div>
              )
          )}
        </div>
      )
    }

    return null
  }

  const getLines = () => {
    return lines.map((line) => (
      <Line
        key={line.dataKey}
        name={line.dataLabel}
        type="linear"
        yAxisId={line.yAxisId ?? 'left'}
        dataKey={line.dataKey}
        stroke={line.dataColor}
        dot={{ fill: line.dataColor }}
        activeDot={{ r: 8 }}
        legendType={!line.hideLegend && line.dataLabel ? 'line' : 'none'}
      />
    ))
  }

  return (
    <div className="w-full mt-8" data-testid={`custom-line-chart`}>
      {isLoading && <Loader />}
      <div className="flex justify-end">
        <ChartDownloadButton getPng={getPng} fileName={fileName} />
      </div>
      <div className="relative pt-8 bg-gray-100">
        <div className="flex flex-col items-center p-2 text-primary ">
          <span className="text-2xl">{title}</span>
          {subTitle && <span className="text-lg underline">{subTitle}</span>}
        </div>
        <div className="overflow-x-auto" ref={measuredRef}>
          {!!calculatedWidth && (
            <ResponsiveContainer minWidth={calculatedWidth} minHeight={420} debounce={1}>
              <LineChart
                ref={ref}
                data={data}
                margin={{
                  top: 45,
                  bottom: 30,
                  right: 80,
                  left: 50,
                }}
              >
                <XAxis
                  dataKey={xAxisDataKey}
                  tick={{ fontSize: 18 }}
                  stroke="green"
                  strokeWidth={3}
                  interval={'preserveStartEnd'}
                  tickFormatter={(value) => {
                    const formattedDate = formatDateString(value, undefined, 'dd/MM')
                    return formattedDate === '-' ? value : formattedDate
                  }}
                >
                  <Label value={labelX} position="insideRight" dy={30} fill="#00683A" fontSize="18px" />
                </XAxis>
                <YAxis
                  yAxisId="left"
                  tick={{
                    fontSize: 18,
                  }}
                  stroke={colorYLeft}
                  strokeWidth={3}
                  dx={-5}
                  tickFormatter={(value) => {
                    return formatNumber(value, formatYLeft) ?? value
                  }}
                >
                  <Label value={labelYLeft} position="insideTopLeft" dy={-40} fill={colorYLeft} fontSize="18px" />
                </YAxis>
                {!disableRightYAxis && (
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fontSize: 18 }}
                    stroke={colorYRight}
                    strokeWidth={3}
                    dx={5}
                    tickFormatter={(value) => {
                      return formatNumber(value, formatYRight) ?? value
                    }}
                  >
                    <Label value={labelYRight} position="insideTopRight" dy={-40} fill={colorYRight} fontSize="18px" />
                  </YAxis>
                )}
                <CartesianGrid
                  horizontal={true}
                  vertical={!!verticalFill}
                  stroke="green"
                  strokeWidth={0.5}
                  verticalFill={verticalFill}
                />

                <ReferenceLine yAxisId="left" y={targetValue} stroke="grey" strokeDasharray="3 3" />
                <Tooltip content={tooltip ?? <CustomTooltip />} filterNull={false} />
                {getLines()}
                <Legend
                  wrapperStyle={{
                    left: '0',
                    right: '0',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                  }}
                  iconSize={20}
                  formatter={(value) => <span className="text-primary-tint text-lg">{value}</span>}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  )
}
